import { Controller } from "@hotwired/stimulus"
import DataLoader from "#lib/data_loader"

// Armored-clothing BUILDER — a JS-side-only modal (native <dialog>, no crud--modal, which
// is TurboCrud-coupled) that assembles ONE armor piece from a base garment + an SP armor
// layer + cosmetic style/quality, then pushes it through the armor collection's addEntry
// (via the cp2020--armor outlet — same path as cold-load and the cyberware spawn). It owns
// NO persistence: it produces a resolved armor entry and hands it off.
//
// The form is RECONSTITUTED from an inert <template> on every open (fresh state each time —
// no stale selections leak between builds). Two catalogs drive it:
//   armored_clothing_bases : { bases: { slug: { name, cost, weight, covers[], leather? } } }
//   armored_clothing       : { spTable: { weight: { sp: {cost, ev} } }, style[], quality[] }
// The SP ladder is shown UNFILTERED (all values any weight allows); a value illegal for the
// chosen base's weight (a '—' cell, absent from spTable[weight]) is caught on Confirm.
//
// COST = round(baseCost * leatherMult? * spTable[weight][sp].cost * style.mult * quality.mult)
// EV   = spTable[weight][sp].ev            WEIGHT = base weight (leather bumps it)
// The built entry matches armor.json's shape so the sorter/SP/EV treat it like any piece:
//   { name, baseSP, ev, cost, weight, covers[], isHard:false, isSkinWeave:false, isIntegral:false }

// A cost multiplier as an inline label, e.g. 1 -> "x1", 1.75 -> "x1.75", 0.8 -> "x0.8".
const mult = (n) => `x${n}`

const HIT_LOCATIONS = ["head", "torso", "rarm", "larm", "rleg", "lleg"]
// Every SP the ladder can ever offer (union across weights) — the select shows all; the
// per-weight legality (which of these exist for the chosen weight) is enforced on confirm.
// SP0 = unarmored clothing (legal for every weight, cost x1, ev 0) — the baseline, first.
const SP_LADDER = [0, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]
const spLabel = (sp) => (sp === 0 ? "SP0 (none)" : `SP${sp}`)

export default class extends Controller {
  static outlets = ["cp2020--armor"]
  static targets = ["overlay", "body", "template", "preview", "error"]

  // --- open / close -----------------------------------------------------------
  // A plain overlay (not a native <dialog>) so slim-select's body-appended dropdown can
  // stack ABOVE the modal (a top-layer <dialog> would render the picker behind it). We
  // supply the affordances a <dialog> gave for free: Escape-to-close + backdrop-close.
  connect() {
    this._onKeydown = (e) => { if (e.key === "Escape" && !this.overlayTarget.hidden) this.close() }
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
  }

  // Reconstitute the form from the template EACH open (fresh, stateless), fill the selects
  // from the catalogs, wire live preview, then show the overlay.
  async open(event) {
    event?.preventDefault?.()
    this._mountForm()
    await this._populate()
    this._recompute()
    this.overlayTarget.hidden = false
  }

  close(event) {
    event?.preventDefault?.()
    this.overlayTarget.hidden = true
    this.bodyTarget.replaceChildren() // drop the form so the next open starts clean
  }

  // Click on the backdrop (outside the panel) closes; clicks inside the panel call stop().
  backdropClose(event) { if (event.target === this.overlayTarget) this.close() }
  stop(event) { event.stopPropagation() }

  // Clone the inert <template> into the overlay body — a brand-new form, no carried state.
  _mountForm() {
    this.bodyTarget.replaceChildren(this.templateTarget.content.cloneNode(true))
  }

  // --- data-driven form fill --------------------------------------------------
  async _populate() {
    const [bases, tables] = await Promise.all([
      DataLoader.systemGet("armored_clothing_bases"),
      DataLoader.systemGet("armored_clothing"),
    ])
    this._bases = bases.bases || {}
    this._tables = tables

    const baseOpts = Object.entries(this._bases)
      .map(([slug, b]) => ({ slug, name: b.name }))
      .sort((a, b) => a.name.localeCompare(b.name))
    this._fillSelect("base", baseOpts.map((o) => [o.slug, o.name]))
    this._fillSelect("sp", SP_LADDER.map((sp) => [String(sp), spLabel(sp)]))
    // Style/quality show their COST multiplier inline (e.g. "Urban Flash (x2)") so the
    // price effect is legible at the point of choice.
    this._fillSelect("style", (tables.style || []).map((s, i) => [String(i), `${s.name} (${mult(s.mult)})`]))
    this._fillSelect("quality", (tables.quality || []).map((q, i) => [String(i), `${q.name} (${mult(q.mult)})`]))

    // Quality defaults to Average (the x1 baseline) rather than the first row (Sub-average).
    this._selectByLabelPrefix("quality", "Average")

    // The base <select> is slim-select (type-searchable); it connected on the empty clone,
    // so redraw it now that its options exist. connect() is async after replaceChildren, so
    // the refresh hook may not be attached yet — wait for it, then refresh.
    this._refreshSlim("base")

    // Leather toggle only makes sense for bases that HAVE a leather variant — enable/disable
    // it as the base changes.
    this._syncLeatherAvailability()
  }

  // Select the option whose label starts with `prefix` (labels carry a "(xN)" suffix).
  _selectByLabelPrefix(name, prefix) {
    const sel = this._field(name)
    const opt = sel && Array.from(sel.options).find((o) => o.textContent.startsWith(prefix))
    if (opt) sel.value = opt.value
  }

  // slim-select redraws from the live <option>s via a slimSelectRefresh() hook the controller
  // attaches on connect. That connect is async (post-clone), so poll briefly for the hook.
  _refreshSlim(name, tries = 20) {
    const sel = this._field(name)
    if (!sel) return
    if (typeof sel.slimSelectRefresh === "function") { sel.slimSelectRefresh(); return }
    if (tries > 0) requestAnimationFrame(() => this._refreshSlim(name, tries - 1))
  }

  _fillSelect(name, pairs) {
    const sel = this._field(name)
    if (!sel) return
    sel.replaceChildren()
    for (const [value, label] of pairs) {
      const opt = document.createElement("option")
      opt.value = value
      opt.textContent = label
      sel.appendChild(opt)
    }
  }

  // --- live preview + build ---------------------------------------------------
  // Any change (base/sp/leather/style/quality) recomputes the preview. Bound via the form's
  // input/change action in the template.
  changed() {
    this._syncLeatherAvailability()
    this._recompute()
  }

  _recompute() {
    const built = this._build()
    this._clearError()
    if (!built.ok) {
      this.previewTarget.textContent = "—"
      return
    }
    const e = built.entry
    // Cost · EV · Weight · Covers (SP is already the value in the dropdown above). Coverage
    // shows only the 6 hit locations the armor math uses; cosmetic-only pieces read "none".
    const covered = e.covers.filter((l) => HIT_LOCATIONS.includes(l))
    this.previewTarget.textContent =
      `${e.cost}eb · EV ${e.ev} · ${e.weight} · ${covered.join(", ") || "none"}`
  }

  confirm(event) {
    event?.preventDefault?.()
    const built = this._build()
    if (!built.ok) {
      this._showError(built.reason)
      return
    }
    if (this.hasCp2020ArmorOutlet) this.cp2020ArmorOutlet.addEntry(built.entry)
    this.close()
  }

  // Resolve the current selections into an armor entry (or a reason it's not buildable).
  // Pure over the form state — used for BOTH the live preview and confirm.
  _build() {
    const base = this._bases?.[this._value("base")]
    if (!base) return { ok: false, reason: "Pick a base garment." }

    const useLeather = base.leather && this._checked("leather")
    const weight = useLeather ? base.leather.weight : base.weight
    const leatherMult = useLeather ? base.leather.costMult : 1

    const sp = this._value("sp")
    const row = this._tables?.spTable?.[weight]?.[sp]
    if (!row) {
      return { ok: false, reason: `SP${sp} is not available for ${weight} material.` }
    }

    const style = this._tables.style[Number(this._value("style"))] || { mult: 1 }
    const quality = this._tables.quality[Number(this._value("quality"))] || { mult: 1 }
    const cost = Math.round(base.cost * leatherMult * row.cost * style.mult * quality.mult)

    // SP0 is unarmored clothing — no armor suffix (a plain "Jacket", not "Jacket (SP0)").
    const spNum = Number(sp)
    const name = `${base.name}${useLeather ? ", Leather" : ""}${spNum > 0 ? ` (SP${sp})` : ""}`
    return {
      ok: true,
      entry: {
        name,
        baseSP: Number(sp),
        ev: row.ev,
        cost,
        weight,
        covers: [...base.covers],
        isHard: false,
        isSkinWeave: false,
        isIntegral: false,
      },
    }
  }

  // Leather checkbox is meaningful only where the base defines a leather variant.
  _syncLeatherAvailability() {
    const box = this._field("leather")
    if (!box) return
    const base = this._bases?.[this._value("base")]
    const has = !!base?.leather
    box.disabled = !has
    if (!has) box.checked = false
  }

  // --- small DOM helpers (scoped to the mounted form) -------------------------
  _field(name) { return this.bodyTarget.querySelector(`[name='${name}']`) }
  _value(name) { return this._field(name)?.value ?? "" }
  _checked(name) { return !!this._field(name)?.checked }

  _showError(msg) { if (this.hasErrorTarget) { this.errorTarget.textContent = msg; this.errorTarget.hidden = false } }
  _clearError() { if (this.hasErrorTarget) { this.errorTarget.textContent = ""; this.errorTarget.hidden = true } }
}
